import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { useMemo } from "react";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { CommandJob, Job } from "~/components/build/Show/lib/types";
import { useSteps } from "~/components/build/Show/lib/useSteps";
import { useJobs } from "~/components/build/Show/lib/useJobs";

/**
 * Hook for getting the current step or job from the URL based on either the step or job UUID.
 */
export default function useCurrentJobOrStep() {
  const { stepUuid, jobUuid } = useQueryParams();
  const { stepsMap } = useSteps();
  const jobs = useJobs();

  // Get the job based on the job UUID.
  const currentJob = useMemo(() => {
    return jobs.get(jobUuid || "");
  }, [jobUuid, jobs]);

  // Get the step based on the step UUID or the job's step UUID.
  const currentStep = useMemo(() => {
    return stepsMap.get(currentJob?.stepUuid || stepUuid || "");
  }, [currentJob?.stepUuid, stepUuid, stepsMap]);

  // Find the latest retry for the job (when applicable).
  const currentLatestJob = useMemo(() => {
    if (currentJob?.type === "script") {
      return findLatestRetry(currentJob, jobs);
    }
    return currentJob;
  }, [currentJob, jobs]);

  return {
    currentStep,
    currentJob,
    currentLatestJob,
  };
}

function findLatestRetry(job: CommandJob, jobs: Map<string, Job>) {
  if (job.retriedInJobUuid) {
    const retry = jobs.get(job.retriedInJobUuid);
    if (retry) {
      return findLatestRetry(retry as CommandJob, jobs);
    }
  }
  return job;
}
